import { MatSort } from '@angular/material/sort';
import { Instance, ZoningCode, ZoningCodeVersion } from '../../models';
import { ITableCell, Task } from '../../../../shared/models';

import { ZoningCodeDialogComponent } from './zoning-code-dialog/zoning-code-dialog.component';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ZoningCodeService, InstanceService, ZoningCodeVersionService, TaskService } from '../../services';
import { IDetailsData } from 'src/app/shared/interfaces/details-data';
import { DetailsEntity } from 'src/app/shared/enums/details-entity-enum';
import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Input } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AttachmentType } from 'src/app/shared';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import { MatTableDataSource } from '@angular/material/table';

@Component({
    standalone: false,
    selector: 'app-zoning-codes-view',
    templateUrl: './zoning-codes-view.component.html',
    styleUrls: ['./zoning-codes-view.component.scss', '../../../../shared/styles/data-grid.scss']
})
export class ZoningCodesViewComponent implements OnInit, OnDestroy, AfterViewInit {
    destroy$ = new Subject<boolean>();

    constructor(
        private readonly dialog: MatDialog,
        private readonly zoningCodeService: ZoningCodeService,
        private readonly snackBar: MatSnackBar,
        private readonly instanceService: InstanceService,
        private readonly zcVersionService: ZoningCodeVersionService,
        private readonly taskService: TaskService
    ) {}

    @ViewChild(MatSort) sort: MatSort;

    @Input()
    instance: Instance;

    dataSource = new MatTableDataSource<ZoningCode>();

    zoningCodes: Observable<ZoningCode[]>;

    isLoadedZCDialog = false;
    isLoadedZCVersionDialog = false;

    zoningCodeCells: ITableCell[] = [
        { property: 'GridicsId', title: 'Gridics Id' },
        { property: 'zoningCodeStatusStr', title: 'ZC Status' },
        { property: 'ActualVersion', title: 'Actual Version' }
    ];
    zoningCodeVersionCells: ITableCell[] = [
        { property: 'Id', title: 'Version Id' },
        { property: 'ActualVersion', title: 'Actual Version' },
        { property: 'PRCalculationResultsCount', title: 'Calculated Property Records' }
    ];

    zoningCodeColumnsToDisplay: string[] = [
        'indexColumn',
        'Name',
        ...this.zoningCodeCells.map(zcCell => zcCell.property),
        'PropertyRecordsCount',
        'MissedEntities',
        'Actions',
        'Status'
    ];
    zoningCodeVersionColumnsToDisplay: string[] = [
        ...this.zoningCodeVersionCells.map(zcvCell => zcvCell.property),
        'Actions',
        'Status'
    ];

    expandedElement: ZoningCode;

    isLoaded: boolean = false;
    isOpen = false;

    private _width: number = 440;
    minWidth: number = 215;
    maxWidth: number = 600;

    detailsData: IDetailsData;

    attachmentType: AttachmentType;

    get width(): number {
        return this._width;
    }

    set width(width: number) {
        if (width >= this.minWidth && width <= this.maxWidth) {
            this._width = width;
        }
    }

    ngOnInit() {
        this.zoningCodes = this.zoningCodeService.zoningCodes;
        this.zoningCodes.subscribe(data => {
            this.dataSource.data = data;
        });
        this.dataSource.filterPredicate = function(data: ZoningCode, filterValue: string) {
            return (
                data.Name?.toString()
                    .trim()
                    .toLowerCase()
                    .indexOf(filterValue) !== -1 ||
                data.GridicsId?.toString()
                    .trim()
                    .toLowerCase()
                    .indexOf(filterValue) !== -1
            );
        };
        this.instanceService.currentInstance.pipe(takeUntil(this.destroy$)).subscribe(() => (this.isOpen = false));
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    doFilter(value: string): void {
        this.dataSource.filter = value.trim().toLocaleLowerCase();
    }

    openZCVersionSideNav(zoningCode: ZoningCode, zoningCodeVersion: ZoningCodeVersion) {
        this.detailsData = {
            detailsEntity: DetailsEntity.ZONING_CODE_VERSION,
            zoningCodeVersion: zoningCodeVersion,
            zoningCode: zoningCode
        };

        this.attachmentType = AttachmentType.ZC_VERSION_FAILED_PR_FOLIOS;

        this.isOpen = !this.isOpen;
    }

    openZCSideNav(zoningCode: ZoningCode) {
        this.detailsData = {
            detailsEntity: DetailsEntity.ZONING_CODE,
            zoningCode: zoningCode
        };

        this.attachmentType = AttachmentType.ZC_INDEXED_PR_FOLIOS;

        this.isOpen = !this.isOpen;
    }

    showVersions(zoningCode: ZoningCode) {
        // this will update ZoningCode statistic if needed
        if (!zoningCode.isPRCountUpdated) {
            this.zoningCodeService.uploadAndSetZoningCodeStatistic(zoningCode).subscribe(() => {});
        }
        if (this.expandedElement && zoningCode.Id === this.expandedElement.Id) {
            this.expandedElement = null;
            return;
        }

        this.expandedElement = zoningCode;

        if (!zoningCode.areVersionsLoaded) {
            this.zoningCodeService.uploadVersionsForZoningCodeAndEmitChanges(zoningCode);
        }
    }

    checkVersion(event: Event, zoningCode: ZoningCode) {
        if (!zoningCode.hasVersionsTasks) {
            this.zoningCodeService.checkZoningCodeVersion(zoningCode);
        } else {
            this.snackBar.open(
                `To check for a new version of Zoning Code, delete the existing calculation process.
                Note: after registering a new version, the calculation of the current version will not be possible.`,
                'OK',
                {
                    verticalPosition: 'top',
                    horizontalPosition: 'center',
                    duration: 6000
                }
            );
        }

        event.stopPropagation();
    }

    indexZCPropertyRecords(event: Event, zoningCode: ZoningCode, offset?: number) {
        this.zoningCodeService.indexZCPropertyRecords({
            instance: this.instanceService.selectedInstance,
            zoningCodeId: zoningCode.Id,
            zcGridicsId: zoningCode.GridicsId,
            offset
        });
        event.stopPropagation();
    }

    reindexZoningCode(event: Event, zoningCode: ZoningCode, offset?: number) {
        const indexingTask = zoningCode.Tasks.find(task => Task.isIndexing(task));

        if (!indexingTask) {
            this.indexZCPropertyRecords(event, zoningCode, offset);
            return;
        }

        this.openReindexZCPropertyRecordsDialog(zoningCode, indexingTask, offset);

        event.stopPropagation();
    }

    getZoningCodeStatisticCallback(zoningCode: ZoningCode) {
        return () => {
            return this.zoningCodeService.uploadAndSetZoningCodeStatistic(zoningCode);
        };
    }

    openDeleteZoningCodeDialog(zoningCode: ZoningCode) {
        if (!zoningCode.Id || !zoningCode.Name) {
            return;
        }

        const dialogConfig = new MatDialogConfig();

        const title = 'Remove Zoning Code';
        const topContent = `You are going to remove Zoning Code ${zoningCode.Name}. All Zoning Code Versions
         and calculation results will be deleted. This operation can not be undone. Continue?`;

        dialogConfig.autoFocus = true;
        dialogConfig.data = { title, topContent };
        dialogConfig.maxHeight = '600px';
        dialogConfig.maxWidth = '300px';

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);
        dialogRef
            .afterClosed()
            .pipe(
                filter(isConfirm => isConfirm),
                switchMap(() => {
                    const hasTasks: boolean = zoningCode.hasTasks();

                    if (hasTasks) {
                        this.snackBar.open(`First you must close all tasks for the selected zoning code.`, 'OK', {
                            verticalPosition: 'top',
                            horizontalPosition: 'center',
                            duration: 4000
                        });

                        return of(null);
                    }

                    zoningCode.setDeletionStatus();

                    this.zoningCodeService.updateZoningCodes();

                    return this.zcVersionService.removeZoningCode(zoningCode.Id);
                }),
                filter(removalStatistic => !!removalStatistic)
            )
            .subscribe(() => {
                this.zoningCodeService.removeZoningCode(zoningCode.Id);

                this.snackBar.open(`Zoning Code ${zoningCode.Name} has been deleted`, 'OK', {
                    verticalPosition: 'top',
                    horizontalPosition: 'center',
                    duration: 4000
                });
                this.instanceService.updateSelectedInstanceStatistic();
            });
    }

    openReindexZCPropertyRecordsDialog(zoningCode: ZoningCode, indexingTask: ITaskModel, offset?: number) {
        const dialogConfig = new MatDialogConfig();

        const title = 'Start reindexing';

        const topContent = 'Indexing is in process.',
            bottomContent = 'Do you want to stop it and start reindexing?',
            okButtonColor = 'warn';

        dialogConfig.autoFocus = true;
        dialogConfig.data = { title, topContent, bottomContent, okButtonColor };
        this.dialog
            .open(ConfirmationDialogComponent, dialogConfig)
            .afterClosed()
            .pipe(
                filter(isConfirm => isConfirm),
                switchMap(() => this.taskService.deleteTask(indexingTask))
            )
            .subscribe(() => {
                this.indexZCPropertyRecords(event, zoningCode, offset);
            });
    }

    openRecalculateTaskDialog(zcVersion: ZoningCodeVersion, zoningCode: ZoningCode) {
        const dialogConfig = new MatDialogConfig();

        const title = 'Start recalculation';

        const topContent = 'The calculation is in process.',
            bottomContent = 'Do you want to stop it and start recalculation?',
            okButtonColor = 'warn';

        dialogConfig.autoFocus = true;
        dialogConfig.data = { title, topContent, bottomContent, okButtonColor };
        const recalculateDialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);
        recalculateDialogRef
            .afterClosed()
            .pipe(filter(isConfirm => isConfirm))
            .subscribe(() => {
                this.zcVersionService.recalculateZCVersion(
                    this.instanceService.selectedInstance,
                    zcVersion,
                    zoningCode,
                    true
                );
            });
    }

    deleteZoningCode(event: Event, zoningCode: ZoningCode) {
        this.openDeleteZoningCodeDialog(zoningCode);
        event.stopPropagation();
    }

    openNewZoningCodeDialog() {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.autoFocus = true;
        dialogConfig.minWidth = '270px';

        const dialogRef = this.dialog.open(ZoningCodeDialogComponent, dialogConfig);

        dialogRef
            .afterClosed()
            .pipe(
                filter(result => result),
                switchMap(zoningCode => this.zoningCodeService.registerNewZoningCodeId(zoningCode))
            )
            .subscribe(() => {
                this.snackBar.open(`Zoning code added successfully`, 'OK', {
                    verticalPosition: 'top',
                    horizontalPosition: 'center',
                    duration: 2000
                });
            });
    }

    setIsLoaded(isLoaded: boolean): void {
        this.isLoaded = isLoaded;
    }

    setIsLoadedZCDialog(isLoadedDialog: boolean, zoningCode): void {
        zoningCode.isWaitingActionFinish = isLoadedDialog;
        this.isLoadedZCDialog = isLoadedDialog;
    }

    setIsLoadedZCVersionDialog(isLoadedDialog: boolean, zoningCode): void {
        zoningCode.isWaitingActionFinish = isLoadedDialog;
        this.isLoadedZCVersionDialog = isLoadedDialog;
    }

    setWidth(width: number): void {
        this.width = width;
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    clickZoningCodeName() {
        this.instanceService.clearCurrentInstance();
    }

    missedEntitiesClick(event) {
        event.stopPropagation();
    }
}
